import { useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { ExpandMore, History, OpenInNew } from '@mui/icons-material';
import dayjs from 'dayjs';
import { IUpdate } from '../data/types';
import { colors } from '../styles';
import SectionHeading from './SectionHeading';

/** שמות החודשים מקובעים — locale של dayjs נקבע גלובלית במסכים אחרים ולא מובטח */
const HEBREW_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const INITIAL_COUNT = 8;

interface UpdatesTimelineProps {
    updates: IUpdate[];
    onOpenFeature: (featureId: string) => void;
}

const UpdatesTimeline = ({ updates, onOpenFeature }: UpdatesTimelineProps) => {
    const [expanded, setExpanded] = useState(false);

    const groups = useMemo(() => {
        const shown = expanded ? updates : updates.slice(0, INITIAL_COUNT);
        const byMonth: { label: string; items: IUpdate[] }[] = [];
        shown.forEach((update) => {
            const date = dayjs(update.date);
            const label = `${HEBREW_MONTHS[date.month()]} ${date.year()}`;
            const last = byMonth[byMonth.length - 1];
            if (last && last.label === label) last.items.push(update);
            else byMonth.push({ label, items: [update] });
        });
        return byMonth;
    }, [updates, expanded]);

    const hidden = updates.length - INITIAL_COUNT;

    return (
        <Box component="section" dir="rtl">
            <SectionHeading
                title="עדכוני גרסה"
                subtitle="כל השיפורים והתוספות שנכנסו לתוכנה, מהחדש לישן."
                icon={<History sx={{ color: '#0f4ea8', fontSize: 30 }} />}
            />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: '22px',
                    border: `1px solid ${colors.border}`,
                    boxShadow: '0 16px 45px rgba(15, 50, 80, 0.10)',
                    overflow: 'hidden'
                }}
            >
                {groups.map((group) => (
                    <Box key={group.label}>
                        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.2, bgcolor: colors.tint }}>
                            <Typography sx={{ fontWeight: 900, color: colors.ink }}>{group.label}</Typography>
                        </Box>
                        {group.items.map((update) => (
                            <Stack
                                key={`${update.title}-${dayjs(update.date).valueOf()}`}
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={{ xs: 0.8, md: 3 }}
                                sx={{
                                    px: { xs: 2, md: 3 },
                                    py: 2.2,
                                    borderTop: `1px solid ${colors.border}`
                                }}
                            >
                                <Box sx={{ minWidth: 110 }}>
                                    <Chip
                                        size="small"
                                        label={dayjs(update.date).format('DD/MM/YYYY')}
                                        sx={{ fontWeight: 800, bgcolor: colors.softBg, color: '#0f4ea8' }}
                                    />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography sx={{ fontWeight: 800, color: colors.ink, lineHeight: 1.5, textAlign: 'start' }}>{update.title}</Typography>
                                    {update.description && (
                                        <Typography sx={{ color: colors.body, lineHeight: 1.8, mt: 0.5, textAlign: 'start' }}>{update.description}</Typography>
                                    )}
                                    {update.featureId && (
                                        <Button
                                            size="small"
                                            startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                                            onClick={() => onOpenFeature(update.featureId as string)}
                                            sx={{ mt: 0.8, fontWeight: 800, px: 0 }}
                                        >
                                            לצפייה ביכולת
                                        </Button>
                                    )}
                                </Box>
                            </Stack>
                        ))}
                    </Box>
                ))}

                {hidden > 0 && (
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: `1px solid ${colors.border}` }}>
                        <Button
                            onClick={() => setExpanded((prev) => !prev)}
                            startIcon={
                                <ExpandMore
                                    sx={{
                                        transform: expanded ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 150ms ease'
                                    }}
                                />
                            }
                            sx={{ fontWeight: 800 }}
                        >
                            {expanded ? 'הצג פחות' : `הצג עוד ${hidden} עדכונים`}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default UpdatesTimeline;
